/**
 * 나눔터 상세의 받기·발표. 라이브러리와 같은 메뉴·발표를 쓰고, 데이터만 스냅샷 가족으로 바꾼다.
 * 개요만 있어도 기존 export/generate-file 로 텍스트 파일을 만든다. 변환(다시 만들기)은 없다.
 * 열람자 user_content 에는 쓰지 않는다(스냅샷).
 */

import React, { useMemo, useState } from 'react';
import {
  remakeTextPptFile,
  remakeTextWorksheetExport,
  remakeTextWorksheetPdf,
} from '../../api/libraryArtifactFiles';
import useFileDownload, { FILE_DOWNLOAD_LABELS } from '../../hooks/useFileDownload';
import { isWorksheetExportEnabled } from '../../utils/artifactHistory';
import {
  buildLibraryDownloadOptions,
  shouldOpenDownloadMenu,
} from '../../utils/libraryDownloadOptions';
import { galleryItemAsFamily } from '../../utils/galleryFamilyItem';
import { filenameForFormat, pickLibraryRemakeOutline } from '../../utils/libraryFamily';
import { canPresentLibraryPpt } from '../chat/presentationSlides';
import PresentationMode from '../chat/PresentationMode';
import LibraryDownloadMenu from '../library/LibraryDownloadMenu';

/** 옵션에 파일명이 없을 때의 저장 이름. 확장자는 옵션 형식을 따른다(학습지라고 .pdf 로 고정하지 않는다). */
const fallbackFilename = (item, format) => {
  const fmt = String(format || '').toLowerCase();
  if (item?.filename) return fmt ? filenameForFormat(item.filename, fmt) : item.filename;
  const title = String(item?.title || 'whatquiz').replace(/[/\\:*?"<>|]/g, '_').slice(0, 50);
  if (fmt) return `${title}.${fmt}`;
  if (item?.content_type === 'ppt') return `${title}.pptx`;
  if (item?.content_type === 'worksheet') return `${title}.pdf`;
  return title || 'whatquiz-file';
};

export const galleryDownloadOptions = (item, { exportEnabled } = {}) => {
  const family = galleryItemAsFamily(item);
  if (!family || (family.content_type !== 'ppt' && family.content_type !== 'worksheet')) {
    return [];
  }
  const built = buildLibraryDownloadOptions({
    item: family,
    conversation: null,
    exportEnabled: exportEnabled ?? isWorksheetExportEnabled(),
  });
  return built.options || [];
};

export default function GalleryFamilyActions({
  item,
  outline = null,
  viewMode = null,
  onTrackDownload,
  onOverlayChange,
}) {
  const family = useMemo(() => galleryItemAsFamily(item), [item]);
  const options = useMemo(() => galleryDownloadOptions(item), [item]);
  const { download, isDownloading, buttonLabel } = useFileDownload();
  const [menuOpen, setMenuOpen] = useState(false);
  const [presentation, setPresentation] = useState(null);
  const [presentChoices, setPresentChoices] = useState(null);

  const notifyOverlay = (open) => {
    onOverlayChange?.(open);
  };

  if (!family || (family.content_type !== 'ppt' && family.content_type !== 'worksheet')) {
    return null;
  }

  const fulfill = async (option) => {
    if (option?.kind === 'direct' && option.url) {
      const result = await download({
        url: option.url,
        filename: option.filename || fallbackFilename(family, option.format),
      });
      if (result.ok && family.id && onTrackDownload) onTrackDownload(family.id);
      return;
    }
    const remakeOutline = pickLibraryRemakeOutline(family, null) || outline;
    const result = await download({
      filename: option.filename || fallbackFilename(family, option.format),
      getFile: async ({ signal }) => {
        if (option.remake?.type === 'ppt-text') {
          return remakeTextPptFile({
            outline: remakeOutline,
            filename: option.filename,
            preferCanvas: Boolean(option.remake.preferCanvas),
            signal,
          });
        }
        if (option.remake?.type === 'ws-export') {
          return remakeTextWorksheetExport({
            outline: remakeOutline,
            format: option.remake.format,
            filename: option.filename,
            signal,
          });
        }
        if (option.remake?.type === 'ws-canvas-pdf') {
          return remakeTextWorksheetPdf({
            outline: remakeOutline,
            filename: option.filename,
            signal,
          });
        }
        if (option.url) return { url: option.url, filename: option.filename };
        throw new Error('받을 수 있는 파일이 없어요');
      },
    });
    if (result.ok && family.id && onTrackDownload) onTrackDownload(family.id);
  };

  const handleDownload = async () => {
    if (!options.length || isDownloading) return;
    if (shouldOpenDownloadMenu(options)) {
      setMenuOpen(true);
      notifyOverlay(true);
      return;
    }
    await fulfill(options[0]);
  };

  const presentable = canPresentLibraryPpt(
    family,
    outline || family.outline,
    viewMode ? { mode: viewMode } : {},
  );
  if (!options.length && !presentable.ok) {
    return null;
  }

  const handlePresent = () => {
    const versions = presentable?.versions || [];
    if (versions.length > 1) {
      setPresentChoices(versions);
      notifyOverlay(true);
      return;
    }
    if (presentable?.slides?.length) setPresentation(presentable.slides);
  };

  const closeMenu = () => {
    setMenuOpen(false);
    notifyOverlay(Boolean(presentChoices?.length || presentation?.length));
  };

  const closePresentChoices = () => {
    setPresentChoices(null);
    notifyOverlay(Boolean(menuOpen || presentation?.length));
  };

  return (
    <>
      {options.length > 0 && (
        <button
          aria-busy={isDownloading || undefined}
          className="gallery-detail-text-btn"
          data-testid="gallery-family-download"
          disabled={isDownloading}
          onClick={handleDownload}
          type="button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {isDownloading ? (buttonLabel || FILE_DOWNLOAD_LABELS.preparing) : '다운로드'}
        </button>
      )}
      {presentable.ok && (
        <button
          className="gallery-detail-text-btn"
          data-testid="gallery-family-present"
          onClick={handlePresent}
          type="button"
        >
          발표 모드
        </button>
      )}
      {menuOpen && (
        <LibraryDownloadMenu
          busy={isDownloading}
          onClose={closeMenu}
          onSelect={async (option) => {
            closeMenu();
            await fulfill(option);
          }}
          open={menuOpen}
          options={options}
          testId="gallery-family-download-menu"
          title="어떤 파일로 받을까요?"
        />
      )}
      {Boolean(presentChoices?.length) && (
        <LibraryDownloadMenu
          onClose={closePresentChoices}
          onSelect={(option) => {
            closePresentChoices();
            if (option?.slides?.length) setPresentation(option.slides);
          }}
          open
          options={presentChoices}
          testId="wqpresent-version-menu"
          title="어떤 버전으로 발표할까요?"
        />
      )}
      {presentation?.length > 0 && (
        <PresentationMode
          onClose={() => {
            setPresentation(null);
            notifyOverlay(Boolean(menuOpen || presentChoices?.length));
          }}
          slides={presentation}
        />
      )}
    </>
  );
}
